import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import {
  Plus, Trash2, ExternalLink, Download, Link2, FileImage, FileCog, Paperclip,
  ShieldCheck, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

const FILE_TYPES = [
  { value: 'CAD', label: 'CAD Drawing' },
  { value: 'Schematic', label: 'Schematic / Circuit Diagram' },
  { value: 'Weld_Drawing', label: 'Weld / Fabrication Drawing' },
  { value: 'Photo', label: 'Photo / Reference Image' },
  { value: 'Datasheet', label: 'Datasheet' },
  { value: 'Other', label: 'Other' },
];

const typeIcon = (t) => {
  switch (t) {
    case 'CAD': return <FileCog className="w-4 h-4 text-blue-600" />;
    case 'Schematic': return <FileCog className="w-4 h-4 text-purple-600" />;
    case 'Weld_Drawing': return <FileCog className="w-4 h-4 text-orange-600" />;
    case 'Photo': return <FileImage className="w-4 h-4 text-emerald-600" />;
    case 'Datasheet': return <Paperclip className="w-4 h-4 text-slate-600" />;
    default: return <Link2 className="w-4 h-4 text-slate-500" />;
  }
};

/**
 * iter73 — Linked production files panel for BOM detail page.
 * Lets engineers attach CAD / schematic / weld drawing files (URL or upload)
 * and mark them "Matched" once they've been confirmed to align with the BOM.
 */
const BomLinkedFilesPanel = ({ api, bomId, files, onChanged, canEdit, requireMatchedToRelease = false }) => {
  const [addOpen, setAddOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [form, setForm] = useState({
    label: '', file_type: 'CAD', url: '', revision: '', file: null,
  });
  // iter77 — templates picker
  const [templates, setTemplates] = useState([]);
  const [pickedTemplateId, setPickedTemplateId] = useState('');

  const resetForm = () => { setForm({ label: '', file_type: 'CAD', url: '', revision: '', file: null }); setPickedTemplateId(''); };

  useEffect(() => {
    if (!addOpen) return;
    api().get('/bom-linked-file-templates').then(r => setTemplates(r.data || [])).catch(() => setTemplates([]));
  }, [addOpen, api]);

  const applyTemplate = async (tplId) => {
    setPickedTemplateId(tplId);
    if (!tplId) return;
    try {
      const { data } = await api().post(`/bom-linked-file-templates/${tplId}/preview`, { bom_id: bomId });
      setForm(s => ({
        ...s,
        file_type: data.file_type || s.file_type,
        label: data.label || s.label,
        url: data.url || s.url,
        revision: data.revision || s.revision,
      }));
      toast.success('Template applied — you can still edit the fields before saving');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not apply template'); }
  };

  const matchedCount = useMemo(() => (files || []).filter(f => f.matched).length, [files]);
  const total = (files || []).length;

  const fileToDataUri = (file) => new Promise((resolve, reject) => {
    // iter78w — hard 10MB cap matches backend + ingress so big phone photos
    // don't silently fail upload.
    if (file.size > 10 * 1024 * 1024) {
      reject(new Error('File too large. Max 10MB.'));
      return;
    }
    const r = new FileReader();
    r.onerror = () => reject(new Error('Could not read file.'));
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });

  const submitAdd = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    if (!form.url && !form.file) { toast.error('Provide a URL or upload a file'); return; }
    // iter76 — normalize URL: auto-prepend https:// if user typed a bare domain,
    // and reject anything that's not http(s) so navigating from the row anchor
    // can never resolve to an in-app route and surface "BOM not found".
    let cleanUrl = (form.url || '').trim();
    if (cleanUrl) {
      const lower = cleanUrl.toLowerCase();
      if (!lower.startsWith('http://') && !lower.startsWith('https://')) {
        if (/^[\w-]+(\.[\w-]+)+(\/.*)?$/.test(cleanUrl)) {
          cleanUrl = 'https://' + cleanUrl;
          toast.info('Auto-added "https://" to the URL');
        } else {
          toast.error('URL must start with http:// or https://');
          return;
        }
      }
    }
    try {
      const body = {
        label: form.label.trim(),
        file_type: form.file_type,
        revision: form.revision || undefined,
      };
      if (cleanUrl) body.url = cleanUrl;
      if (form.file) {
        if (form.file.size > 10 * 1024 * 1024) { toast.error('Inline files are capped at 10 MB — please use a URL instead'); return; }
        body.file_data = await fileToDataUri(form.file);
        body.file_name = form.file.name;
      }
      await api().post(`/boms/${bomId}/linked-files`, body);
      toast.success('File linked to BOM');
      setAddOpen(false); resetForm();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add file'); }
  };

  const toggleMatched = async (file) => {
    setBusyId(file.id);
    try {
      await api().put(`/boms/${bomId}/linked-files/${file.id}/match`, { matched: !file.matched });
      toast.success(!file.matched ? 'Marked as matched ✓' : 'Match removed');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusyId(null); }
  };

  const remove = async (file) => {
    if (!window.confirm(`Remove "${file.label}"? Linked files are part of the BOM record.`)) return;
    try {
      await api().delete(`/boms/${bomId}/linked-files/${file.id}`);
      toast.success('Linked file removed');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadFile = async (file) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/boms/${bomId}/linked-files/${file.id}/download`;
    // Use api() so auth header is attached; stream as blob
    try {
      const r = await api().get(`/boms/${bomId}/linked-files/${file.id}/download`, { responseType: 'blob' });
      const blob = new Blob([r.data]);
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = file.file_name || `${file.label}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      // If it was a URL-only entry the redirect should open it; fall back to window.open
      if (file.url) window.open(file.url, '_blank');
      else toast.error('Download failed');
    }
    // eslint-disable-next-line no-unused-expressions
    url;
  };

  return (
    <Card className="industrial-card" data-testid="bom-linked-files-panel">
      <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
        <div>
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Link2 className="w-4 h-4 text-blue-600" /> Linked Production Files
            {total > 0 && (
              <Badge variant="outline" className="ml-2 text-[10px]" data-testid="bom-linked-files-count">
                {matchedCount}/{total} matched
              </Badge>
            )}
          </h2>
          <p className="text-xs text-slate-500 mt-1">CAD, schematics, weld drawings, photos & datasheets that this BOM is matched with.</p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={() => { resetForm(); setAddOpen(true); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bom-add-linked-file-btn">
            <Plus className="w-4 h-4 mr-1" /> Add File
          </Button>
        )}
      </CardHeader>
      {/* iter74 — release gate status banner */}
      {requireMatchedToRelease && (
        <div className={`px-4 py-2 text-xs border-b flex items-center gap-2 ${matchedCount > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`} data-testid="bom-linked-files-gate-banner">
          {matchedCount > 0 ? <ShieldCheck className="w-4 h-4 shrink-0" /> : <ShieldAlert className="w-4 h-4 shrink-0" />}
          {matchedCount > 0 ? (
            <span><strong>Release gate satisfied.</strong> {matchedCount} matched file(s) — production may proceed.</span>
          ) : (
            <span><strong>Release blocked.</strong> This BOM requires at least one linked file marked <em>Matched</em> before it can be released. Admins can override.</span>
          )}
        </div>
      )}
      <CardContent className="p-0">
        {total === 0 ? (
          <p className="text-sm text-slate-400 italic p-6 text-center">No production files linked yet. Click <strong>Add File</strong> to attach a CAD drawing, schematic, weld drawing, or photo.</p>
        ) : (
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Rev</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Matched</TableHead>
                <TableHead>Uploaded</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {files.map(f => (
                <TableRow key={f.id} data-testid={`bom-linked-file-row-${f.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {typeIcon(f.file_type)}
                      <span className="text-xs text-slate-600">{(f.file_type || '').replace('_', ' ')}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{f.label}</TableCell>
                  <TableCell className="mono text-xs">{f.revision || '—'}</TableCell>
                  <TableCell className="text-xs">
                    {f.url ? (
                      <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1 mono truncate max-w-xs">
                        <ExternalLink className="w-3 h-3" /> URL
                      </a>
                    ) : (
                      <span className="text-slate-700 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {f.file_name || 'Uploaded'}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {f.matched ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 text-xs" title={`Matched ${f.matched_at ? 'on ' + new Date(f.matched_at).toLocaleDateString() : ''} by ${f.matched_by_name || ''}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Matched
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                        <ShieldAlert className="w-3.5 h-3.5" /> Not matched
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-[11px] text-slate-500">
                    {f.uploaded_at ? new Date(f.uploaded_at).toLocaleDateString() : '—'}
                    {f.uploaded_by_name && <div className="mono">{f.uploaded_by_name}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => downloadFile(f)} title="Open / download" data-testid={`bom-linked-file-open-${f.id}`}>
                        {f.url ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => toggleMatched(f)} disabled={busyId === f.id} className={f.matched ? 'text-emerald-700' : 'text-amber-700'} title={f.matched ? 'Unmark match' : 'Mark as matched'} data-testid={`bom-linked-file-match-${f.id}`}>
                          <ShieldCheck className="w-4 h-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => remove(f)} className="text-red-600 hover:bg-red-50" title="Remove" data-testid={`bom-linked-file-delete-${f.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Add file dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => { setAddOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="bom-add-linked-file-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="w-5 h-5 text-blue-600" /> Add Production File</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {templates.length > 0 && (
              <div className="rounded-sm border border-blue-200 bg-blue-50/40 p-2.5 space-y-1" data-testid="bom-template-picker-container">
                <Label className="text-xs text-blue-900 font-medium">Start from a template (optional)</Label>
                <Select value={pickedTemplateId} onValueChange={applyTemplate}>
                  <SelectTrigger className="rounded-sm bg-white" data-testid="bom-linked-file-template-select"><SelectValue placeholder="Pick a saved template…" /></SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id} data-testid={`bom-linked-file-template-option-${t.id}`}>
                        {t.name} <span className="text-[10px] text-slate-400 ml-2">({t.file_type})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-blue-900/70">All fields below will pre-fill — you can still tweak them before saving.</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">File Type *</Label>
                <Select value={form.file_type} onValueChange={(v) => setForm(s => ({ ...s, file_type: v }))}>
                  <SelectTrigger className="rounded-sm" data-testid="bom-add-file-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FILE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Revision</Label>
                <Input value={form.revision} onChange={(e) => setForm(s => ({ ...s, revision: e.target.value }))} placeholder="Rev A, v3, etc." className="rounded-sm mono" data-testid="bom-add-file-revision-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Label *</Label>
              <Input value={form.label} onChange={(e) => setForm(s => ({ ...s, label: e.target.value }))} placeholder="e.g., Welding Fixture Drawing" className="rounded-sm" data-testid="bom-add-file-label-input" />
            </div>
            <div>
              <Label className="text-xs">External URL (CAD server, Drive, SharePoint…)</Label>
              <Input value={form.url} onChange={(e) => setForm(s => ({ ...s, url: e.target.value, file: e.target.value ? null : s.file }))} placeholder="https://..." className="rounded-sm mono text-xs" data-testid="bom-add-file-url-input" />
            </div>
            <div className="text-center text-[11px] text-slate-400">— or —</div>
            <div>
              <Label className="text-xs">Upload file (≤ 10 MB)</Label>
              <input
                type="file"
                onChange={(e) => setForm(s => ({ ...s, file: e.target.files?.[0] || null, url: e.target.files?.[0] ? '' : s.url }))}
                className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-sm file:border-0 file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer w-full mt-1"
                data-testid="bom-add-file-upload-input"
              />
              {form.file && <div className="text-[11px] text-slate-500 mt-1 mono">Selected: {form.file.name} ({(form.file.size / 1024).toFixed(1)} KB)</div>}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setAddOpen(false); resetForm(); }}>Cancel</Button>
              <Button onClick={submitAdd} className="bg-slate-900 hover:bg-slate-800" data-testid="bom-add-file-submit-btn">
                <Plus className="w-4 h-4 mr-1" /> Link File
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default BomLinkedFilesPanel;
